import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  InputAdornment
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EmailIcon from '@mui/icons-material/Email';
import NotificationsIcon from '@mui/icons-material/Notifications';
import api from '../../api/axios';

interface NotificationSettingsData {
  id: number;
  notification_email: string;
  enabled: boolean;
  include_appointments: boolean;
  include_cancellations: boolean;
  include_modifications: boolean;
  include_chat_messages: boolean;
  chat_notification_email: string;
  polling_interval_minutes: number;
  lookback_hours: number;
  created_at: string;
  updated_at: string | null;
}

const NotificationSettings: React.FC = () => {
  const [settings, setSettings] = useState<NotificationSettingsData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<NotificationSettingsData>>({});
  
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/settings/notification');
      
      setSettings(response.data);
      setFormData(response.data);
    } catch (err: any) {
      console.error('Error fetching notification settings:', err);
      setError(err.response?.data?.error || 'Failed to fetch notification settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof NotificationSettingsData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSwitchChange = (field: keyof NotificationSettingsData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(field, event.target.checked);
  };

  const handleTextChange = (field: keyof NotificationSettingsData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(field, event.target.value);
  };

  const handleNumberChange = (field: keyof NotificationSettingsData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const numValue = parseInt(event.target.value);
    if (!isNaN(numValue) && numValue > 0) {
      handleChange(field, numValue);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    try {
      setSaving(true);
      setError(null);
      
      const response = await api.post('/api/settings/notification', formData);
      
      setSettings(response.data);
      setSuccessMessage('Notification settings saved successfully');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      console.error('Error saving notification settings:', err);
      setError(err.response?.data?.error || 'Failed to save notification settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestEmail = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const response = await api.post('/api/settings/notification/test', { 
        email: formData.notification_email 
      });
      
      setSuccessMessage('Test email sent successfully');
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      console.error('Error sending test email:', err);
      setError(err.response?.data?.error || 'Failed to send test email');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        <NotificationsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Notification Settings
      </Typography>
      
      <Typography variant="body1" paragraph>
        Configure email notifications for new appointments, cancellations, chat messages and other events.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mt: 2 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Email Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} md={8}>
              <TextField
                label="Notification Email"
                fullWidth
                required
                value={formData.notification_email || ''}
                onChange={handleTextChange('notification_email')}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', height: '100%', alignItems: 'center' }}>
                <Button 
                  variant="outlined" 
                  onClick={handleSendTestEmail}
                  disabled={saving || !formData.notification_email}
                  sx={{ mt: 0 }}
                >
                  {`Send Test To ${formData.notification_email ? formData.notification_email : 'This Email'}`}
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enabled || false}
                    onChange={handleSwitchChange('enabled')}
                    color="primary"
                  />
                }
                label="Enable email notifications"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Notification Types
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.include_appointments || false}
                    onChange={handleSwitchChange('include_appointments')}
                    color="primary"
                  />
                }
                label="New appointments"
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.include_cancellations || false}
                    onChange={handleSwitchChange('include_cancellations')}
                    color="primary"
                  />
                }
                label="Cancelled appointments"
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.include_modifications || false}
                    onChange={handleSwitchChange('include_modifications')}
                    color="primary"
                  />
                }
                label="Modified appointments"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Polling Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary" paragraph>
                These settings control how often the system checks for new appointments from Cliniko.
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Polling Interval (minutes)"
                type="number"
                fullWidth
                value={formData.polling_interval_minutes || 15}
                onChange={handleNumberChange('polling_interval_minutes')}
                variant="outlined"
                inputProps={{ min: 5, max: 60 }}
                helperText="How often to check for new appointments (5-60 minutes)"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Lookback Period (hours)"
                type="number"
                fullWidth
                value={formData.lookback_hours || 24}
                onChange={handleNumberChange('lookback_hours')}
                variant="outlined"
                inputProps={{ min: 1, max: 72 }}
                helperText="How far back to check for appointments (1-72 hours)"
              />
            </Grid>
            
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                disabled={saving}
                sx={{ mr: 2 }}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
      
      <Snackbar 
        open={!!successMessage} 
        autoHideDuration={5000} 
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationSettings;